import { createFileRoute, useMatch } from '@tanstack/react-router'
import { SETTINGS_STACK } from './-components/settings/settings-section'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useDeferredValue } from 'react'
import { useLingui } from '@lingui/react/macro'
import { CompanyIcon } from '@/routes/_app/-components/settings/settings-icons'
import { formatDateLocale, formatNumber } from '@/lib/utils'
import { useTranslation } from '@/lib/locales'
import { useRootSearch } from '@/hooks/use-root-search'
import { useDebouncedSearch } from '@/hooks/use-debounced-search'
import { SandboxResetHint, UnlinkCompanyAction } from './-components/api/entity-row-actions'
import { IntegrationListPanel, IntegrationRow, MetaText } from './-components/api/integration-list'
import { Avatar } from './-components/shared/avatar'
import { IntegrationListPending } from './-components/settings/account-pending'

export const Route = createFileRoute('/_app/account/integrations/$appId/companies')({
  component: RouteComponent,
  pendingComponent: IntegrationListPending,
  // Painted on the click frame; safe only because the searched list key lives on the pathless `_list` layout below.
})

function RouteComponent() {
  const { language } = useTranslation()
  const { t, i18n } = useLingui()
  const { q } = useRootSearch()
  const { appId } = Route.useParams()
  const { searchInput, handleSearchChange } = useDebouncedSearch({ initialValue: q })

  const { context } = useMatch({ from: '/_app/account/integrations/$appId/companies/_list' })
  // Defer the OPTIONS object (the context is reference-stable per match), so a new search keeps the
  // previous page on screen while the next one loads instead of flashing the skeleton.
  const deferredOptions = useDeferredValue(context.appCompaniesQueryOptions)
  const { data: companies, isFetching } = useSuspenseQuery(deferredOptions)
  // True while the deferred options lag the live ones — keeps the dimming through that transition.
  const isStale = isFetching || deferredOptions !== context.appCompaniesQueryOptions

  const total = companies.pagination.count
  // Plural chosen on the number, shown formatted.
  const count = formatNumber(i18n.locale, total, 0)
  return (
    <div className={SETTINGS_STACK}>
      <SandboxResetHint />
      <IntegrationListPanel
        entity="companies"
        query={searchInput}
        onQueryChange={handleSearchChange}
        count={total === 1 ? t`${count} company` : t`${count} companies`}
        isStale={isStale}
        isEmpty={companies.list.length === 0}
        empty={{
          noResults: Boolean(q),
          icon: <CompanyIcon className="text-muted-foreground size-5" />,
          title: t`No companies yet`,
          description: t`Companies your integration creates or links through the API show here.`,
        }}
        pagination={companies.pagination}
      >
        {companies.list.map((elem) => {
          const name = elem.name ?? elem.email ?? t`Unnamed`
          return (
            <IntegrationRow
              key={elem.id}
              media={<Avatar size="md" type="company" name={name} forceInitials subtle />}
              title={name}
              meta={
                <>
                  {elem.email && elem.name && <MetaText>{elem.email}</MetaText>}
                  {elem.created && <span className="shrink-0">{formatDateLocale(elem.created, language)}</span>}
                </>
              }
              actions={<UnlinkCompanyAction appClientId={appId} companyId={elem.id} name={name} />}
            />
          )
        })}
      </IntegrationListPanel>
    </div>
  )
}
